import React from 'react';
import { X, Download, ExternalLink } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';

interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  document: {
    id: string;
    nome_original: string;
    url_arquivo: string;
    tipo_mime?: string | null;
    tipo_documento: string;
  };
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  isOpen,
  onClose,
  document
}) => {
  if (!isOpen) return null;

  const isPDF = document.tipo_mime?.includes('pdf') || document.nome_original.toLowerCase().endsWith('.pdf');
  const isImage = document.tipo_mime?.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(document.nome_original);
  const isWord = document.tipo_mime?.includes('word') || document.tipo_mime?.includes('document') || 
                 /\.(doc|docx)$/i.test(document.nome_original);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Resolve URL with Supabase signed URL when needed (for private buckets)
  const [resolvedUrl, setResolvedUrl] = React.useState(document.url_arquivo);

  const extractBucketAndPath = (urlStr: string): { bucket: string; path: string } | null => {
    try {
      const u = new URL(urlStr);
      const parts = u.pathname.split('/').filter(Boolean); // ['storage','v1','object','public','bucket','path','to','file.pdf']
      const objectIdx = parts.findIndex(p => p === 'object');
      if (objectIdx >= 0 && parts.length > objectIdx + 2) {
        // visibility segment may be 'public' or 'sign' or 'auth'
        const bucket = parts[objectIdx + 2];
        const path = parts.slice(objectIdx + 3).join('/');
        return { bucket, path };
      }
      return null;
    } catch {
      return null;
    }
  };

  React.useEffect(() => {
    let isMounted = true;
    const maybeSign = async () => {
      const info = extractBucketAndPath(document.url_arquivo);
      if (!info) {
        setResolvedUrl(document.url_arquivo);
        return;
      }
      try {
        const { data, error } = await supabase.storage.from(info.bucket).createSignedUrl(info.path, 60 * 60);
        if (!error && data?.signedUrl && isMounted) {
          setResolvedUrl(data.signedUrl);
        } else {
          setResolvedUrl(document.url_arquivo);
        }
      } catch {
        setResolvedUrl(document.url_arquivo);
      }
    };
    if (isOpen) maybeSign();
    return () => { isMounted = false; };
  }, [document.url_arquivo, isOpen]);

  const getViewerContent = () => {
    if (isPDF) {
      return (
        <iframe
          src={resolvedUrl}
          className="w-full h-full border-0"
          title={document.nome_original}
        />
      );
    }

    if (isImage) {
      return (
        <div className="flex items-center justify-center h-full bg-black">
          <img
            src={resolvedUrl}
            alt={document.nome_original}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    if (isWord) {
      // Usar Google Docs Viewer para documentos Word
      const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(resolvedUrl)}&embedded=true`;
      return (
        <iframe
          src={googleViewerUrl}
          className="w-full h-full border-0"
          title={document.nome_original}
        />
      );
    }

    // Para outros tipos de arquivo, mostrar opções de download
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <div className="bg-muted rounded-lg p-8 max-w-md">
          <h3 className="text-lg font-medium mb-4">
            Visualização não disponível
          </h3>
          <p className="text-muted-foreground mb-6">
            Este tipo de arquivo não pode ser visualizado diretamente no navegador.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.open(resolvedUrl, '_blank')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              <Download className="h-4 w-4" />
              Baixar Arquivo
            </button>
            <button
              onClick={() => window.open(resolvedUrl, '_blank')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Abrir em Nova Aba
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-background rounded-lg shadow-2xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground truncate">
              {document.nome_original}
            </h2>
            <p className="text-sm text-muted-foreground">
              {document.tipo_documento} • Visualização do documento
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(resolvedUrl, '_blank')}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              title="Baixar arquivo"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              title="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {getViewerContent()}
        </div>

        {/* Footer com informações */}
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {isPDF && "Visualizando PDF diretamente no navegador"}
              {isImage && "Visualizando imagem"}
              {isWord && "Visualizando via Google Docs Viewer"}
              {!isPDF && !isImage && !isWord && "Arquivo não suportado para visualização"}
            </span>
            <button
              onClick={() => window.open(resolvedUrl, '_blank')}
              className="flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              Abrir original
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;